import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { Layers, Plus } from "lucide-react";
import { CompositionTab, AddRecordDialog } from "../components/ghg/CompositionTab";
import { RelevanceTab } from "../components/ghg/RelevanceTab";
import { Scope2Tab } from "../components/ghg/Scope2Tab";
import { Scope3Tab } from "../components/ghg/Scope3Tab";

export default function GHGInventory() {
  const [year, setYear] = useState(2026);
  const [comp, setComp] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [activations, setActivations] = useState(null);
  const [scope2, setScope2] = useState(null);
  const [scope3, setScope3] = useState(null);
  const [re100, setRe100] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const role = (() => {
    try { return JSON.parse(localStorage.getItem("lestari_user") || "{}").role; } catch { return null; }
  })();
  const canEdit = ["tenant_admin", "esg_manager"].includes(role);
  const canWrite = ["tenant_admin", "esg_manager", "data_officer"].includes(role);

  const loadComp = useCallback(() => {
    api.get(`/ghg/scope1?year=${year}`).then((r) => setComp(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [year]);
  const loadScope2 = useCallback(() => {
    api.get(`/ghg/scope2?year=${year}`).then((r) => setScope2(r.data)).catch(() => {});
  }, [year]);
  const loadScope3 = useCallback(() => {
    api.get(`/ghg/scope3?year=${year}`).then((r) => setScope3(r.data)).catch(() => {});
  }, [year]);
  const loadRe100 = useCallback(() => {
    api.get("/ghg/re100").then((r) => setRe100(r.data)).catch(() => {});
  }, []);
  const loadActivations = useCallback(() => {
    api.get("/ghg/activations").then((r) => { setActivations(r.data); setCatalog(r.data.catalog || []); }).catch(() => {});
  }, []);
  useEffect(loadComp, [loadComp]);
  useEffect(loadScope2, [loadScope2]);
  useEffect(loadScope3, [loadScope3]);
  useEffect(loadRe100, [loadRe100]);
  useEffect(loadActivations, [loadActivations]);

  const reload = () => { loadComp(); loadScope2(); loadScope3(); loadRe100(); loadActivations(); };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="ghg-inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Data Management · GHG Inventory</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3">
            <Layers className="h-8 w-8 text-accent" /> GHG Inventory
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            GHG Protocol-aligned Scope 1 / 2 / 3 capture, drill-down and relevance screening. Every source is
            classified and screened, so unused ones read “screened — not material” rather than disappearing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <Button variant="primary" onClick={() => setAddOpen(true)} data-testid="ghg-add-record-btn">
              <Plus className="h-4 w-4" /> Add Scope 1 record
            </Button>
          )}
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="ghg-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[2026, 2025, 2024].map((y) => <SelectItem key={y} value={String(y)}>FY{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="composition">
        <TabsList className="grid grid-cols-4 max-w-3xl">
          <TabsTrigger value="composition" data-testid="ghg-tab-composition">Scope 1 Composition</TabsTrigger>
          <TabsTrigger value="scope2" data-testid="ghg-tab-scope2">Scope 2 (LB vs MB)</TabsTrigger>
          <TabsTrigger value="scope3" data-testid="ghg-tab-scope3">Scope 3 Screening</TabsTrigger>
          <TabsTrigger value="relevance" data-testid="ghg-tab-relevance">Scope 1 Relevance</TabsTrigger>
        </TabsList>

        <TabsContent value="composition" className="mt-4">
          <CompositionTab comp={comp} year={year} />
        </TabsContent>
        <TabsContent value="scope2" className="mt-4">
          <Scope2Tab d={scope2} re100={re100} canEdit={canEdit} onSaved={reload} />
        </TabsContent>
        <TabsContent value="scope3" className="mt-4">
          <Scope3Tab d={scope3} canEdit={canEdit} onSaved={reload} />
        </TabsContent>
        <TabsContent value="relevance" className="mt-4">
          <RelevanceTab activations={activations} canEdit={canEdit} onSaved={reload} />
        </TabsContent>
      </Tabs>

      <AddRecordDialog open={addOpen} onOpenChange={setAddOpen} catalog={catalog}
        onSaved={() => { setAddOpen(false); reload(); }} />
    </div>
  );
}
